<template>
	<div v-if="jpgAssets?.length" class="w-[26.875rem] mt-2 flex items-center gap-3">
		<button
			type="button"
			class="flex-none btn btn"
			:disabled="currentIndex <= 1"
			aria-label="Předchozí strana letáku"
			@click="movePrev"
		>
			<NuxtIcon name="chevron-left" filled class="text-2xl" />
		</button>

		<div ref="wrapperRef" class="flex-1 overflow-hidden">
			<div ref="listRef" class="flex min-w-max">
				<div
					v-for="(leaflet, index) in jpgAssets"
					:key="index"
					class="!h-auto w-24 flex-none cursor-pointer flex flex-col group pt-1 h-full px-1"
					:class="{'border-border-active': index === internalIndex, 'border-transparent': index !== internalIndex}"
					@click="() => onSlideClick(index + 1)"
				>
					<div
						class="grow rem:border-[3px] mb-2 transition-colors ring-1 ring-border-tertiary shadow-xs"
						:class="{'border-border-active': index === internalIndex, 'border-transparent': index !== internalIndex}"
					>
						<NuxtImg
							:src="getLeafletThumbnailUrl(leaflet)"
							width="96"
							height="128"
							densities="x1"
							loading="lazy"
							:alt="`${index + 1}. strana akčního letáku`"
						/>
					</div>
					<span class="text-center block typo-body-s">
						{{ computerToHumanIndexing(index) }}
					</span>
				</div>
			</div>
		</div>

		<button
			type="button"
			class="flex-none rounded-full bg-white text-icon-primary hover:bg-background-secondary disabled:bg-white disabled:text-body-disabled p-2 shadow-sm hover:shadow-xs hover:disabled:shadow-sm"
			:disabled="currentIndex >= jpgAssets.length"
			aria-label="Další strana letáku"
			@click="moveNext"
		>
			<NuxtIcon name="chevron-right" filled class="text-2xl" />
		</button>
	</div>
</template>

<script setup lang="ts">
	import {ref, onMounted, onBeforeUnmount, watch, computed} from 'vue';
	import {gsap} from 'gsap';
	import {Draggable} from 'gsap/Draggable';
	import {InertiaPlugin} from 'gsap/InertiaPlugin';
	import {useWindowSize} from '@vueuse/core';
	import type {Gsoa_ActionOffers_StoreDocument as GsoaActionOffersStoreDocument} from '~/openapi';
	import {computerToHumanIndexing} from '@/utils';

	gsap.registerPlugin(Draggable, InertiaPlugin);

	interface Props {
		jpgAssets: GsoaActionOffersStoreDocument['jpgAssets'];
		currentIndex: number;
	}
	interface Emits {
		(update: 'update:index', index: number): void
	}
	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const listRef = ref<HTMLElement | null>(null);
	const wrapperRef = ref<HTMLElement | null>(null);
	const draggable = ref<Draggable | null>(null);

	const internalIndex = computed(() => props.currentIndex - 1);

	function getLeafletThumbnailUrl(assetUrl: string): string {
		const url = new URL(assetUrl);
		url.searchParams.set('type', '3');
		return url.toString();
	}

	function movePrev() {
		if (props.currentIndex > 1) {
			emit('update:index', props.currentIndex - 1);
		}
	}

	function moveNext() {
		if (props.jpgAssets && props.currentIndex < props.jpgAssets.length) {
			emit('update:index', props.currentIndex + 1);
		}
	}

	function onSlideClick(index: number) {
		emit('update:index', index);
	}

	function scrollToCurrentIndex() {
		const slideWidth = listRef.value!.children[internalIndex.value].clientWidth;
		const containerWidth = wrapperRef.value!.clientWidth;
		const listWidth = listRef.value!.scrollWidth;

		let scrollX;

		if (props.currentIndex === 1) {
			scrollX = 0;
		} else if (props.jpgAssets && props.currentIndex === props.jpgAssets.length) {
			scrollX = listWidth - containerWidth;
		} else {
			scrollX = slideWidth * internalIndex.value - (containerWidth / 2) + (slideWidth / 2);
			scrollX = Math.min(Math.max(scrollX, 0), listWidth - containerWidth);
		}

		gsap.to(listRef.value, {x: -scrollX, duration: 1, ease: 'power2.out'});
	}

	function setupDraggable() {
		draggable.value = new Draggable(listRef.value!, {
			type: 'x',
			bounds: wrapperRef.value,
			inertia: true,
			onDrag() {},
		});
	}

	onMounted(() => {
		setupDraggable();
		scrollToCurrentIndex();
	});

	onBeforeUnmount(() => {
		draggable.value?.kill();
	});

	const {width} = useWindowSize();
	watch(width, () => {
		draggable.value?.kill();
		setupDraggable();
	});

	watch(
		() => props.currentIndex,
		() => {
			scrollToCurrentIndex();
		},
	);
</script>
